import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { categoryColor, prettyType, SCORE_TONE } from "@/lib/intel";
import { toast } from "sonner";
import { ArrowLeft, Network as NetworkIcon, CheckCircle2, Flag, FileSearch, ShieldQuestion } from "lucide-react";

function ScoreBadge({ score, testid }) {
  return (
    <span data-testid={testid || `intel-score-${score.level}`} className={`inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-md text-sm font-medium font-mono ${SCORE_TONE[score.level] || SCORE_TONE.low}`}>
      <span className="uppercase tracking-wider">{score.level}</span>
      <span className="opacity-70">· {score.score}/100</span>
    </span>
  );
}

export default function EntityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEntity = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/intel/entities/${id}`); setE(data); }
    catch { toast.error("Entity not found"); navigate("/intel/entities"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetchEntity(); }, [fetchEntity]);

  const review = async (status) => {
    try { await api.patch(`/intel/entities/${id}/review`, { review_status: status }); setE((p) => ({ ...p, review_status: status })); toast.success(`Marked ${status}`); }
    catch { toast.error("Could not update review"); }
  };

  if (loading || !e) return <div className="p-6 text-sm text-slate-500">Loading entity…</div>;

  const attrs = Object.entries(e.attributes || {}).filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0));

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="entity-detail-page">
      <button onClick={() => navigate(-1)} data-testid="entity-back-btn" className="text-xs text-slate-500 hover:text-slate-900 font-mono uppercase tracking-wider inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      <header className="flex flex-col lg:flex-row gap-4 lg:items-start justify-between">
        <div className="space-y-2 min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: categoryColor(e.category) }} />
            <span className="text-xs font-mono uppercase tracking-wider text-slate-500">{prettyType(e.type)}</span>
            {e.synthetic && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500">DEMO</span>}
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl">{e.label}</h1>
          <div className="flex flex-wrap gap-2 items-center">
            <ScoreBadge score={e.score} />
            {e.network && <span className="text-xs font-mono text-slate-500">{e.network}</span>}
            <span className={`text-[11px] font-mono px-2 py-0.5 rounded-md border ${e.review_status === "approved" ? "bg-teal-50 text-teal-700 border-teal-200" : e.review_status === "flagged" ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
              {e.review_status}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/intel/graph?focus=${e.id}`} data-testid="entity-open-graph" className="inline-flex items-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-md">
            <NetworkIcon className="h-4 w-4" /> Explore network
          </Link>
          {!readOnly && (
            <>
              <Button data-testid="review-approve" variant="outline" className="border-slate-300 gap-1.5" onClick={() => review("approved")}><CheckCircle2 className="h-4 w-4 text-teal-600" /> Approve</Button>
              <Button data-testid="review-flag" variant="outline" className="border-slate-300 gap-1.5" onClick={() => review("flagged")}><Flag className="h-4 w-4 text-red-600" /> Flag</Button>
            </>
          )}
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          <section className="border border-slate-200 bg-white rounded-md p-5">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3">Identity intelligence</h2>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Source</span><span className="font-mono text-xs">{e.source}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Confidence</span><span className="font-mono text-xs">{Math.round((e.confidence || 0) * 100)}%</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Last verified</span><span className="font-mono text-xs">{(e.last_verified || "").slice(0, 10)}</span></div>
              {(e.roles || []).length > 0 && <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Roles</span><span className="font-mono text-xs">{e.roles.map(prettyType).join(", ")}</span></div>}
              {attrs.map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 py-1 gap-4">
                  <span className="text-slate-500 shrink-0">{prettyType(k)}</span>
                  <span className="font-mono text-xs text-right break-all">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="connections-section">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3">Network connections ({e.connections.length})</h2>
            <div className="divide-y divide-slate-100">
              {e.connections.map((c) => (
                <Link key={c.relationship_id} to={`/intel/entities/${c.entity.id}`} data-testid={`connection-${c.entity.id}`}
                  className="flex items-center gap-3 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-md">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: categoryColor(c.entity.category) }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{c.entity.label}</div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      {c.direction === "out" ? "" : "← "}{prettyType(c.type)}{c.direction === "out" ? " →" : ""} · {prettyType(c.entity.type)}
                    </div>
                  </div>
                  <span className="text-[11px] text-slate-400 font-mono">{Math.round(c.confidence * 100)}%</span>
                </Link>
              ))}
              {e.connections.length === 0 && <div className="text-xs text-slate-400 py-2">No connections mapped.</div>}
            </div>
          </section>

          <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="evidence-section">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3 flex items-center gap-2"><FileSearch className="h-4 w-4" /> Evidence explorer ({e.evidence.length})</h2>
            <p className="text-xs text-slate-500 mb-3">Every connection traces back to a source. Nothing is hidden.</p>
            <div className="space-y-2">
              {e.evidence.map((ev, i) => (
                <div key={ev.id || `${ev.kind}-${ev.timestamp || ""}-${i}`} data-testid={`evidence-${i}`} className="border border-slate-100 rounded-md p-3 bg-slate-50 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] uppercase text-slate-500">{ev.kind} · via {prettyType(ev.via)}</span>
                    <span className="font-mono text-[11px] text-slate-400">{(ev.timestamp || "").slice(0, 10)}</span>
                  </div>
                  <div className="text-slate-700 mt-1">{ev.detail || ev.ref || "—"}</div>
                  {ev.url && <a href={ev.url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline break-all">{ev.url}</a>}
                </div>
              ))}
              {e.evidence.length === 0 && <div className="text-xs text-slate-400">No evidence items attached.</div>}
            </div>
          </section>
        </div>

        <aside>
          <section className="border border-slate-200 bg-white rounded-md p-5">
            <h2 className="font-display font-bold text-lg tracking-tight mb-1 flex items-center gap-2"><ShieldQuestion className="h-4 w-4" /> Intelligence score</h2>
            <div className="text-xs text-slate-500 mb-3">Explainable · reviewable</div>
            <div className="mb-3"><ScoreBadge score={e.score} testid="intel-score-summary" /></div>
            <ul className="space-y-3">
              {e.score.reasons.map((r, i) => (
                <li key={r.code || `reason-${i}`} data-testid={`score-reason-${r.code}`} className="border-l-2 border-slate-200 pl-3 text-sm">
                  <div className="flex items-center justify-between"><span className="font-medium">{r.label}</span><span className="text-xs font-mono text-slate-500">+{r.points}</span></div>
                  <div className="text-xs text-slate-500">{r.detail}</div>
                  <div className="text-[10px] text-slate-400 font-mono">confidence {Math.round((r.confidence || 0) * 100)}%</div>
                </li>
              ))}
              {e.score.reasons.length === 0 && <li className="text-xs text-slate-400">No scoring signals yet.</li>}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
